<template>
  <div>
    <v-navigation-drawer
      permanent
      left
      app
      clipped
      width="300"
    >
      <template #prepend>
        <div class="pt-4 px-2 mb-2 d-flex align-center" style="gap: .75rem;">
          <v-btn
            small
            text
            :to="relationRoute"
            exact
          >
            <v-icon> mdi-arrow-left </v-icon>
          </v-btn>
          <v-btn
            class="ml-auto"
            small
            text
            :loading="loading"
            @click="fetchData"
          >
            Opdater
            <v-icon right> mdi-sync </v-icon>
          </v-btn>
          <v-btn
            v-if="!locked"
            :loading="locking"
            :disabled="!!spec.lockedOn"
            small
            text
            @click="lock"
          >
            Lås
            <v-icon right> mdi-lock-outline </v-icon>
          </v-btn>

          <v-btn
            v-else
            :loading="locking"
            text
            small
            @click="unlock"
          >
            Lås op
            <v-icon right> mdi-lock-open-outline </v-icon>
          </v-btn>
        </div>
      </template>

      <v-list dense nav open-strategy="multiple">
        <v-list-item :to="specBaseRoute" exact>
          <v-list-item-avatar>
            <v-icon> mdi-home-outline </v-icon>
          </v-list-item-avatar>

          <v-list-item-title>
            Oversigt
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-if="hasFeature('Approval')" :to="specBaseRoute + '/approval'" exact>
          <v-list-item-avatar>
            <v-icon> mdi-script-text-outline </v-icon>
          </v-list-item-avatar>

          <v-list-item-title>
            Leveranceaftale
          </v-list-item-title>
        </v-list-item>

        <v-list-item :to="specBaseRoute + '/resources'" exact>
          <v-list-item-avatar>
            <v-icon> mdi-finance </v-icon>
          </v-list-item-avatar>

          <v-list-item-title>
            Resurseudgifter
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-if="hasFeature('Invoicing')" :to="specBaseRoute + '/invoicing'" exact>
          <v-list-item-avatar>
            <v-icon> mdi-receipt </v-icon>
          </v-list-item-avatar>
          <v-list-item-title>
            Fakturering
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-if="hasFeature('Travel')" :to="specBaseRoute + '/travel'" exact>
          <v-list-item-avatar>
            <v-icon> mdi-airplane </v-icon>
          </v-list-item-avatar>

          <v-list-item-title>
            Rejseudgifter
          </v-list-item-title>
        </v-list-item>

        <template v-if="hasFeature('MaterialsFlat')">
          <v-list-item v-if="spec.groups[0]" :to="`${specBaseRoute}/group/${spec.groups[0].number}/section/${spec.groups[0].sections[0]?.number}`">
            <v-list-item-avatar>
              <v-icon> mdi-file-document-outline </v-icon>
            </v-list-item-avatar>
            <v-list-item-title>
              Materialer
            </v-list-item-title>
          </v-list-item>
        </template>

        <template v-if="hasFeature('MaterialsSegmented')">
          <v-list-group
            v-for="(group) in spec.groups"
            :key="group.name"
            :color="$vuetify.breakpoint.mobile ? 'white' : ''"
            @click="$router.push(`${specBaseRoute}/group/${group.number}`)"
          >
            <template #activator>
              <v-list-item-avatar>
                <v-icon> mdi-folder-outline </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ group.number }} {{ group.name }}
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <v-list-item
              v-for="(section) in group.sections"
              :key="section.title"
              class="pl-6"
              :to="`${specBaseRoute}/group/${group.number}/section/${section.number}`"
            >
              <v-list-item-avatar>
                <v-icon> mdi-file-document-outline </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ group.number }}.{{ section.number }} {{ section.title }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <div class="text-caption">
                  {{ section.quantity }}
                </div>
              </v-list-item-action>
            </v-list-item>
            <SpecificationSectionCreate
              :group="group"
              :locked="locked"
              :spec="spec"
            />
          </v-list-group>
          <v-divider></v-divider>
          <SpecificationSectionGroupCreate
            :locked="locked"
            :spec="spec"
          />
        </template>
      </v-list>

      <template #append>
        <v-list dense nav>
          <v-list-item :to="specBaseRoute + '/messages'" exact>
            <v-list-item-avatar>
              <v-icon> mdi-message-outline </v-icon>
            </v-list-item-avatar>
            <v-list-item-title>
              Beskeder
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-progress-linear indeterminate :active="loading || saving"></v-progress-linear>

    <router-view
      v-if="spec"
      :approvals="approvals"
      :spec="spec"
      :relations="relations"
      :loading="loading"
      :locked="locked"
      :saving="saving"
      :spec-base-route="specBaseRoute"
      @update="fetchData"
      @section:delete="fetchData"
      @section:update="fetchData"
      @group:edit="fetchData"
    />

    <SpecificationMessages
      :spec="spec"
      :show="showMessages"
      :messages="messages"
      :relations="relations"
      @click:close="showMessages = false"
    />
  </div>
</template>

<script>
import SpecificationSectionCreate from '@/components/specification/SpecificationSectionCreate.vue';
import SpecificationSectionGroupCreate from '@/components/specification/SpecificationSectionGroupCreate.vue';
import SpecificationMessages from '@/components/specification/SpecificationMessages.vue';

export default {
  name: 'Specification',
  components: {
    SpecificationSectionCreate,
    SpecificationSectionGroupCreate,
    SpecificationMessages
  },
  data() {
    return {
      approvals: [],
      creating: false,
      loading: false,
      loadingApprovals: false,
      locking: false,
      messages: [],
      saving: false,
      showMessages: false,
      spec: null
    }
  },
  computed: {
    relationRoute() {
      const { relationId, pathMatch } = this.$route.params;

      return `/${pathMatch}/${relationId}`;
    },
    specBaseRoute() {
      const { relationId, specId, pathMatch } = this.$route.params;

      return `/${pathMatch}/${relationId}/specification/${specId}`;
    },
    locked() {
      // If manually locked, it's locked
      if (this.spec.lockedOn) {
        return true;
      }

      else if (this.approvals.length) {
        const latest = this.approvals[0];

        // Lock the spec if..
        // .. an approval has been approved
        if (this.approvals.some(x => x.approved)) {
          return true;
        }

        // .. the latest approval has been sent but not responded
        else if (latest.sentOn && !latest.respondedOn) {
          return true;
        }

        else {
          return false;
        }
      }

      else {
        return false;
      }
    },
    relations() {
      if (!this.spec.groups) return [];

      else {
        const spec = [this.spec.id, this.spec.title];
        const sections = this.spec.groups.flatMap(group => group.sections.map(section => [section.id, section.title]));

        return [spec, ...sections];
      }
    }
  },
  async mounted() {
    this.getApprovals();
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      const { specId } = this.$route.params;

      try {
        this.loading = true;

        const response = await this.$api.get(`specification/${specId}`);

        if (!response.data.success || !response.data.model) {
          throw response.data.message ?? 'Error getting specification';
        }

        this.spec = response.data.model;
        this.$store.commit('specification/setCurrentSpecification', response.data.model);
      } catch (error) {
        this.$notifier.showError({});
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async getApprovals() {
      this.loadingApprovals = true;
      const { specId } = this.$route.params;

      try {
        const response = await this.$api.get(`approval/component`, {
          params: {
            relationKind: 'specification',
            relationId: specId
          }
        });

        const approvals = response.data?.data?.approvals;
        const sorted = approvals.sort((a, b) => {
          return new Date(b.createdOn) - new Date(a.createdOn);
        });

        this.approvals = sorted;
      } catch (error) {
        this.$notifier.showError({});
        console.error(error);
      } finally {
        this.loadingApprovals = false;
      }
    },

    async createSection() {
      try {
        this.creating = true;

        this.newSection.specification_Id = this.spec.id;
        this.newSection.mainNumber = this.spec.sections.length ? (Math.max(...this.spec.sections.map(section => section.mainNumber))) + 1 : 1;
        this.newSection.subNumber = 0;

        const response = await this.$api.post('specification/section', this.newSection);

        if (!response.data.success) throw 'Error creating section';

        this.spec.sections.push(response.data.model);
        this.newSectionMenu = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.creating = false;
      }
    },

    hasFeature(feature) {
      if (!this.spec?.features?.length) return false

      return this.spec.features.includes(feature)
    },

    lock: async function() {
      this.locking = true;

      try {
        await this.$store.dispatch('specification/lock', this.$route.params.specId);
        this.fetchData();
      } finally {
        this.locking = false;
      }
    },

    unlock: async function() {
      this.locking = true;

      try {
        await this.$store.dispatch('specification/unlock', this.$route.params.specId);
        this.fetchData();
      } finally {
        this.locking = false;
      }
    },
  }
}
</script>
